/* Floating SDR sales chat for PPC landings.
   Wire a real agent with VITE_BORG_SALES_CHAT_URL (POST JSON).
   When unset, the widget stays hidden. The reply helper retains a stub for tests. */

export type ChatRole = 'assistant' | 'user' | 'system'

export type ChatMessage = {
  role: ChatRole
  content: string
}

export type SalesChatConfig = {
  /** Backend chat endpoint. Empty string hides the widget. */
  endpoint: string
  /** Where "Book a demo" CTAs send visitors. */
  demoUrl: string
  /** Business plans / talk-to-sales page. */
  salesUrl: string
  agentName: string
  welcome: string
  quickReplies: string[]
}

export type ChatRequestBody = {
  messages: ChatMessage[]
  page?: string
  sessionId?: string
}

export type ChatResponseBody = {
  reply: string
  quickReplies?: string[]
}

declare global {
  interface ImportMetaEnv {
    readonly VITE_BORG_SALES_CHAT_URL?: string
    readonly VITE_BORG_SALES_DEMO_URL?: string
  }
}

export function resolveSalesChatConfig(
  env: Pick<ImportMetaEnv, 'VITE_BORG_SALES_CHAT_URL' | 'VITE_BORG_SALES_DEMO_URL'> = import.meta.env,
): SalesChatConfig {
  const endpoint = (env.VITE_BORG_SALES_CHAT_URL ?? '').trim()
  const demoUrl = (env.VITE_BORG_SALES_DEMO_URL ?? '/borg-for-business/#contact').trim()
  return {
    endpoint,
    demoUrl,
    salesUrl: '/borg-for-business/',
    agentName: 'Borg Sales',
    welcome:
      "Hi. I'm the Borg sales agent. I can walk you through the collective, help you see if it's a fit, and connect you with the team for a demo. What brings you in today?",
    quickReplies: [
      'What is Borg?',
      'Team plans and pricing',
      'Book a demo',
      'How is this different from ChatGPT seats?',
    ],
  }
}

/** Local stub: high-level product answers and qualification prompts. Not live inference. */
export function stubSalesReply(userText: string, config: SalesChatConfig): ChatResponseBody {
  const q = userText.toLowerCase()

  if (/demo|call|meeting|talk to (sales|someone|a human)|book/.test(q)) {
    return {
      reply:
        `Happy to set that up. Book a live walkthrough here: ${config.demoUrl}\n\n` +
        `Or browse business plans at ${config.salesUrl} first. Roughly how many people would use Borg, and is this for one team or the whole company?`,
      quickReplies: ['Under 20 people', '20 to 100', '100+', 'Just exploring'],
    }
  }

  if (/pric|plan|cost|seat|\$|dollar/.test(q)) {
    return {
      reply:
        'Team is $500/month for a shared collective with memory, missions, permissions and budgets. ' +
        'Business is $5000/month and includes guided setup, installation and training. ' +
        'Enterprise is custom when you need API, governance reviews or private models. ' +
        `Details live on ${config.salesUrl}. Want a demo, or more on how pricing compares to per-seat chat tools?`,
      quickReplies: ['Book a demo', 'Compare to ChatGPT seats', 'What is Borg?'],
    }
  }

  if (/chatgpt|copilot|claude\.?ai|per-?seat|different|vs|versus|compar/.test(q)) {
    return {
      reply:
        'Most tools sell you a chat window per person. Borg is a company collective: shared memory, missions, model choice and oversight in one place. ' +
        'Leaders see work, cost and who is moving the needle, instead of only seat counts. ' +
        'You still use frontier models. The difference is governance and a team that remembers how your company works. Want plans, or a demo?',
      quickReplies: ['Team plans and pricing', 'Book a demo', 'What is Borg?'],
    }
  }

  if (/what is|what's borg|explain|product|collective|how (does|do) /.test(q)) {
    return {
      reply:
        'Borg is a collective of AI agents that uses every frontier model, learns how your company works and finds the next thing worth doing. ' +
        'Desktop and web, with shared memory, missions and an app marketplace. ' +
        'For companies, Borg for Business adds team plans, setup and training. What are you trying to solve first: cost, oversight, or getting the whole team on one layer?',
      quickReplies: ['Cost and oversight', 'Team rollout', 'Book a demo'],
    }
  }

  if (/cost|oversight|govern|budget|visibility|rollout|team/.test(q)) {
    return {
      reply:
        'Got it. Many teams start with model spend they cannot see, or a rollout where everyone picks their own tool. ' +
        'Borg puts people on the right models with permissions and budgets, and ties work to the numbers you run the company on. ' +
        `If that sounds close, grab a demo at ${config.demoUrl}, or tell me your team size and stack and I can point you.`,
      quickReplies: ['Book a demo', 'Team plans and pricing', 'Under 20 people'],
    }
  }

  if (/\b(\d+|under 20|20 to 100|100\+|exploring)\b/.test(q)) {
    return {
      reply:
        'Thanks. That helps. Next step is usually a short demo so we can map Borg to your stack and who would own the rollout. ' +
        `Book here: ${config.demoUrl}\n\nAnything else you want answered before you talk to the team?`,
      quickReplies: ['What is Borg?', 'Team plans and pricing', 'Book a demo'],
    }
  }

  return {
    reply:
      "I can cover what Borg is, team plans, how we differ from per-seat chat tools, and how to book a demo. " +
      'Ask in plain language, or tap a suggestion below.',
    quickReplies: config.quickReplies,
  }
}

export async function requestSalesReply(
  messages: ChatMessage[],
  config: SalesChatConfig,
  opts: { page?: string; sessionId?: string; fetchImpl?: typeof fetch } = {},
): Promise<ChatResponseBody> {
  const lastUser = [...messages].reverse().find((m) => m.role === 'user')
  const userText = lastUser?.content?.trim() ?? ''

  if (!config.endpoint) {
    return stubSalesReply(userText, config)
  }

  const fetchImpl = opts.fetchImpl ?? fetch
  const body: ChatRequestBody = {
    messages: messages.filter((m) => m.role === 'user' || m.role === 'assistant'),
    page: opts.page,
    sessionId: opts.sessionId,
  }

  const res = await fetchImpl(config.endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  })

  if (!res.ok) {
    throw new Error(`Sales chat endpoint returned ${res.status}`)
  }

  const data = (await res.json()) as Partial<ChatResponseBody>
  if (!data.reply || typeof data.reply !== 'string') {
    throw new Error('Sales chat endpoint returned no reply')
  }
  return {
    reply: data.reply,
    quickReplies: Array.isArray(data.quickReplies) ? data.quickReplies.map(String) : undefined,
  }
}

function escapeHtml(text: string): string {
  return text
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
}

function linkify(text: string): string {
  const escaped = escapeHtml(text)
  return escaped
    .replace(/(https?:\/\/[^\s<]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>')
    .replace(
      /(^|[\s>(])(\/(?:borg-for-business|questionnaire|ai-at-scale|ai-app-marketplace|lp\/sdr)\/?(?:#contact)?)/g,
      '$1<a href="$2">$2</a>',
    )
    .replaceAll('\n', '<br>')
}

function newSessionId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return `sdr-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

export type MountSalesChatOptions = {
  root?: ParentNode
  config?: SalesChatConfig
  page?: string
}

export function mountSalesChat(options: MountSalesChatOptions = {}): { destroy: () => void } {
  const root = options.root ?? document
  const config = options.config ?? resolveSalesChatConfig()
  // A scripted demo must never appear to be an available sales service.
  if (!config.endpoint.trim()) return { destroy: () => {} }
  const existing = root.querySelector<HTMLElement>('[data-sales-chat]')
  if (existing) {
    return {
      destroy: () => {
        existing.remove()
      },
    }
  }

  const page = options.page ?? (typeof location !== 'undefined' ? location.pathname : '/lp/sdr/')
  const sessionId = newSessionId()
  const messages: ChatMessage[] = [{ role: 'assistant', content: config.welcome }]
  let busy = false
  let open = false

  const host = document.createElement('div')
  host.dataset.salesChat = ''
  host.className = 'sales-chat'
  host.innerHTML = `
    <button type="button" class="sales-chat-launcher" data-sales-chat-launcher aria-expanded="false" aria-controls="sales-chat-panel">
      <span class="sales-chat-launcher-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M4 6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5v7A2.5 2.5 0 0 1 17.5 16H10l-4.2 3.2c-.7.5-1.8 0-1.8-.9V6.5Z"/><path d="M8 9h8M8 12h5"/></svg>
      </span>
      <span class="sales-chat-launcher-label">Chat with sales</span>
    </button>
    <section id="sales-chat-panel" class="sales-chat-panel" data-sales-chat-panel hidden role="dialog" aria-modal="false" aria-labelledby="sales-chat-title">
      <header class="sales-chat-head">
        <div class="sales-chat-identity">
          <span class="sales-chat-avatar" aria-hidden="true">
            <svg class="kite" viewBox="0 0 360 344"><use href="#kite"/></svg>
          </span>
          <div>
            <h2 id="sales-chat-title" class="sales-chat-title">${escapeHtml(config.agentName)}</h2>
            <p class="sales-chat-status" data-sales-chat-status>Online · demo-ready answers</p>
          </div>
        </div>
        <button type="button" class="sales-chat-close" data-sales-chat-close aria-label="Close chat">
          <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true"><path d="M5 5l10 10M15 5 5 15" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>
        </button>
      </header>
      <div class="sales-chat-log" data-sales-chat-log role="log" aria-live="polite"></div>
      <div class="sales-chat-suggestions" data-sales-chat-suggestions></div>
      <form class="sales-chat-form" data-sales-chat-form>
        <label class="sr-only" for="sales-chat-input">Message</label>
        <textarea id="sales-chat-input" class="sales-chat-input" data-sales-chat-input rows="1" placeholder="Ask about Borg, plans, or a demo" autocomplete="off"></textarea>
        <button type="submit" class="sales-chat-send" data-sales-chat-send aria-label="Send">
          <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true"><path d="M4 10h10M10 4l6 6-6 6" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
      </form>
      <p class="sales-chat-note">Illustrative sales agent${config.endpoint ? '' : ' · local stub until VITE_BORG_SALES_CHAT_URL is set'}.</p>
    </section>
  `

  const mountParent = root instanceof Document ? root.body : (root as ParentNode)
  mountParent.appendChild(host)

  const launcher = host.querySelector<HTMLButtonElement>('[data-sales-chat-launcher]')!
  const panel = host.querySelector<HTMLElement>('[data-sales-chat-panel]')!
  const closeBtn = host.querySelector<HTMLButtonElement>('[data-sales-chat-close]')!
  const log = host.querySelector<HTMLElement>('[data-sales-chat-log]')!
  const suggestions = host.querySelector<HTMLElement>('[data-sales-chat-suggestions]')!
  const form = host.querySelector<HTMLFormElement>('[data-sales-chat-form]')!
  const input = host.querySelector<HTMLTextAreaElement>('[data-sales-chat-input]')!
  const sendBtn = host.querySelector<HTMLButtonElement>('[data-sales-chat-send]')!
  const status = host.querySelector<HTMLElement>('[data-sales-chat-status]')!

  function setOpen(next: boolean) {
    open = next
    panel.hidden = !next
    launcher.setAttribute('aria-expanded', String(next))
    host.toggleAttribute('data-open', next)
    if (next) {
      input.focus()
      log.scrollTop = log.scrollHeight
    } else {
      launcher.focus()
    }
  }

  function renderMessages() {
    log.innerHTML = messages
      .filter((m) => m.role !== 'system')
      .map((m) => {
        const who = m.role === 'assistant' ? config.agentName : 'You'
        return `<article class="sales-chat-bubble sales-chat-bubble-${m.role}" data-role="${m.role}">
          <p class="sales-chat-who">${escapeHtml(who)}</p>
          <div class="sales-chat-text">${linkify(m.content)}</div>
        </article>`
      })
      .join('')
    log.scrollTop = log.scrollHeight
  }

  function renderSuggestions(items: string[]) {
    if (!items.length) {
      suggestions.innerHTML = ''
      suggestions.hidden = true
      return
    }
    suggestions.hidden = false
    suggestions.innerHTML = items
      .map(
        (label) =>
          `<button type="button" class="sales-chat-chip" data-sales-chat-chip>${escapeHtml(label)}</button>`,
      )
      .join('')
  }

  function setBusy(next: boolean) {
    busy = next
    sendBtn.disabled = next
    input.disabled = next
    status.textContent = next ? 'Thinking...' : 'Online · demo-ready answers'
  }

  async function sendUserText(raw: string) {
    const text = raw.trim()
    if (!text || busy) return
    messages.push({ role: 'user', content: text })
    renderMessages()
    renderSuggestions([])
    input.value = ''
    setBusy(true)
    try {
      const response = await requestSalesReply(messages, config, { page, sessionId })
      messages.push({ role: 'assistant', content: response.reply })
      renderMessages()
      renderSuggestions(response.quickReplies ?? [])
    } catch {
      messages.push({
        role: 'assistant',
        content:
          `I could not reach the sales agent just now. You can still book a demo at ${config.demoUrl} or see plans at ${config.salesUrl}.`,
      })
      renderMessages()
      renderSuggestions(['Book a demo', 'Team plans and pricing'])
    } finally {
      setBusy(false)
      input.focus()
    }
  }

  renderMessages()
  renderSuggestions(config.quickReplies)

  const onLauncher = () => setOpen(!open)
  const onClose = () => setOpen(false)
  const onSubmit = (e: Event) => {
    e.preventDefault()
    void sendUserText(input.value)
  }
  const onChip = (e: Event) => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-sales-chat-chip]')
    if (!btn) return
    void sendUserText(btn.textContent ?? '')
  }
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && open) {
      e.preventDefault()
      setOpen(false)
    }
    if (e.key === 'Enter' && !e.shiftKey && e.target === input) {
      e.preventDefault()
      void sendUserText(input.value)
    }
  }

  launcher.addEventListener('click', onLauncher)
  closeBtn.addEventListener('click', onClose)
  form.addEventListener('submit', onSubmit)
  suggestions.addEventListener('click', onChip)
  document.addEventListener('keydown', onKey)

  return {
    destroy: () => {
      launcher.removeEventListener('click', onLauncher)
      closeBtn.removeEventListener('click', onClose)
      form.removeEventListener('submit', onSubmit)
      suggestions.removeEventListener('click', onChip)
      document.removeEventListener('keydown', onKey)
      host.remove()
    },
  }
}


/** Wire any [data-open-sales-chat] controls to open the floating panel. */
export function bindSalesChatOpeners(root: ParentNode = document): void {
  root.querySelectorAll<HTMLElement>('[data-open-sales-chat]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault()
      const launcher = document.querySelector<HTMLButtonElement>('[data-sales-chat-launcher]')
      if (!launcher) return
      if (launcher.getAttribute('aria-expanded') !== 'true') launcher.click()
      else launcher.focus()
    })
  })
}
